import React, { useState } from 'react';
import { Package, Check, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { SUPPLIERS } from './constants';
import { PhotoViewer } from './PhotoViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AtelierPartsOrder = ({
  woId, workOrder, employees,
  atelierPartsOrders, deliveryBulletins,
  onOrdersChanged, onVehicleReady,
}) => {
  const [showPartsOrderFlow, setShowPartsOrderFlow] = useState(false);
  const [partsOrderStep, setPartsOrderStep] = useState('pin');
  const [partsOrderPin, setPartsOrderPin] = useState('');
  const [partsOrderEmployee, setPartsOrderEmployee] = useState(null);
  const [partsOrderSupplier, setPartsOrderSupplier] = useState('');
  const [partsOrderNumber, setPartsOrderNumber] = useState('');
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState('');
  const [showDeliveryNoteDialog, setShowDeliveryNoteDialog] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showViewer, setShowViewer] = useState(false);

  const openViewer = (photos, startIndex = 0) => {
    setViewerPhotos(photos);
    setViewerIndex(startIndex);
    setShowViewer(true);
  };

  const startPartsOrderFlow = () => {
    setShowPartsOrderFlow(true);
    setPartsOrderStep('pin');
    setPartsOrderPin('');
    setPartsOrderEmployee(null);
    setPartsOrderSupplier('');
    setPartsOrderNumber('');
  };

  const verifyPartsOrderPin = async () => {
    if (!partsOrderPin || partsOrderPin.length !== 4) { toast.error('Code PIN 4 chiffres requis'); return; }
    try {
      for (const emp of employees) {
        try {
          const res = await axios.post(`${API}/public/timeclock/verify-pin`, { employee_id: emp.id, pin_code: partsOrderPin });
          if (res.status === 200 && res.data?.employee_id) {
            setPartsOrderEmployee(emp);
            setPartsOrderStep('supplier');
            toast.success(`Authentifie: ${emp.first_name} ${emp.last_name}`);
            return;
          }
        } catch { /* try next employee */ }
      }
      toast.error('Code PIN incorrect');
    } catch { toast.error('Erreur de verification'); }
  };

  const selectSupplierAndOpen = (supplier) => {
    setPartsOrderSupplier(supplier.id);
    window.open(supplier.url, '_blank');
    setPartsOrderStep('order_number');
  };

  const submitPartsOrder = async () => {
    if (!partsOrderNumber.trim()) { toast.error('Numero de commande requis'); return; }
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/atelier-parts-order`, {
        employee_id: partsOrderEmployee.id, pin_code: partsOrderPin,
        supplier: partsOrderSupplier, order_number: partsOrderNumber.trim(),
      });
      toast.success(`Commande ${partsOrderNumber} enregistree chez ${partsOrderSupplier}`);
      setShowPartsOrderFlow(false);
      onOrdersChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const submitNothingOrdered = async () => {
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/atelier-parts-order`, {
        employee_id: partsOrderEmployee.id, pin_code: partsOrderPin, nothing_ordered: true,
      });
      toast.success('Aucune commande - vous pouvez continuer');
      setShowPartsOrderFlow(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleMarkVehicleReady = async () => {
    const orderIds = atelierPartsOrders.map(o => o.id);
    const bulletinOrderIds = new Set(deliveryBulletins.filter(b => b.bulletin_number).map(b => b.order_id));
    const missingBulletins = orderIds.filter(id => !bulletinOrderIds.has(id));

    if (missingBulletins.length > 0 && !deliveryNoteNumber.trim()) {
      setShowDeliveryNoteDialog(true);
      return;
    }
    await markVehicleReadyWithNote(deliveryNoteNumber.trim());
  };

  const markVehicleReadyWithNote = async (noteNumber) => {
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/vehicle-ready`, {
        mechanic_name: partsOrderEmployee?.first_name ? `${partsOrderEmployee.first_name} ${partsOrderEmployee.last_name}` : 'Atelier',
        delivery_note_number: noteNumber || '',
      });
      toast.success('Vehicule marque PRET !');
      setShowDeliveryNoteDialog(false);
      onVehicleReady();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <>
      {workOrder.status !== 'completed' && (
        <div className="space-y-3" data-testid="atelier-parts-order-section">
          <Button className="w-full h-16 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 shadow-lg" onClick={startPartsOrderFlow} data-testid="atelier-order-parts-btn">
            <Package className="w-6 h-6 mr-3" /> Commander des pieces
          </Button>

          {atelierPartsOrders.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Commandes atelier</Label>
              {atelierPartsOrders.map((o, i) => {
                const hasBulletin = deliveryBulletins.some(b => b.order_id === o.id);
                return (
                  <div key={o.id || i} className={`rounded-lg px-3 py-2 text-sm border ${hasBulletin ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`atelier-order-${i}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-slate-900">{o.supplier}</span>
                        <span className="ml-2 font-mono text-amber-700 font-bold">#{o.order_number}</span>
                        {hasBulletin && <span className="ml-2 text-[10px] bg-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded font-medium">BL OK</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {o.employee_name} — {o.ordered_at ? new Date(o.ordered_at).toLocaleString('fr-CH', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : ''}
                      </div>
                    </div>
                    {!hasBulletin && (
                      <div className="mt-2 flex items-center gap-3">
                        <img src={`${API}/atelier/work-orders/${woId}/order-qr/${o.id}`} alt="QR" className="w-16 h-16 rounded border" />
                        <div className="text-xs text-slate-500">
                          <p className="font-medium text-slate-700">Scanner ce QR pour photographier le bulletin</p>
                          <a href={`/scan-bulletin/${woId}/${o.id}`} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`scan-bulletin-link-${i}`}>Ou cliquer ici</a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {deliveryBulletins.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Bulletins de livraison
              </Label>
              {(() => {
                const grouped = {};
                deliveryBulletins.forEach(b => {
                  const key = b.bulletin_number || b.id;
                  if (!grouped[key]) grouped[key] = { ...b, photos: [] };
                  grouped[key].photos.push(b);
                });
                return Object.values(grouped).map((group, i) => (
                  <div key={group.id || i} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`bulletin-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500">{group.supplier}</span>
                        <span className="font-mono text-slate-700">BL #{group.bulletin_number}</span>
                        <span className="text-xs text-slate-400">Cmd #{group.order_number}</span>
                        {group.photos.length > 1 && (
                          <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                            {group.photos.length} photos
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      {group.photos.map((b, j) => (
                        <button key={b.id || j}
                          onClick={() => openViewer(group.photos.map(p => `${BACKEND_URL}${p.file_url}`), j)}
                          className="w-12 h-12 rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer"
                          data-testid={`bulletin-photo-${i}-${j}`}>
                          <img src={`${BACKEND_URL}${b.file_url}`} alt={`BL ${j + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* Parts Order Flow Dialog */}
      <Dialog open={showPartsOrderFlow} onOpenChange={(open) => { if (!open && partsOrderStep === 'order_number') { toast.error('Entrez le numero de commande ou cliquez "Rien commande"'); return; } if (!open && partsOrderStep !== 'order_number') setShowPartsOrderFlow(false); }}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => { if (partsOrderStep === 'order_number') e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              {partsOrderStep === 'pin' && 'Identification — Code PIN'}
              {partsOrderStep === 'supplier' && 'Choisir le fournisseur'}
              {partsOrderStep === 'order_number' && `Numero de commande — ${partsOrderSupplier}`}
            </DialogTitle>
          </DialogHeader>

          {partsOrderStep === 'pin' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Entrez votre code PIN personnel (meme que pour le pointage)</p>
              <Input type="password" maxLength={4} inputMode="numeric" pattern="[0-9]*" value={partsOrderPin}
                onChange={(e) => setPartsOrderPin(e.target.value.replace(/\D/g, ''))} placeholder="_ _ _ _"
                className="text-center text-3xl tracking-[1em] h-16 font-mono" autoFocus data-testid="parts-order-pin-input"
                onKeyDown={(e) => { if (e.key === 'Enter') verifyPartsOrderPin(); }} />
              <Button className="w-full h-12" onClick={verifyPartsOrderPin} data-testid="parts-order-pin-submit">Valider le PIN</Button>
            </div>
          )}

          {partsOrderStep === 'supplier' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Authentifie : <strong>{partsOrderEmployee?.first_name} {partsOrderEmployee?.last_name}</strong></p>
              <p className="text-sm text-slate-500">Cliquez sur un fournisseur pour ouvrir son site et passer commande :</p>
              <div className="space-y-2">
                {SUPPLIERS.map(s => (
                  <Button key={s.id} variant="outline"
                    className={`w-full h-16 text-sm font-bold border-2 border-${s.color}-200 bg-white hover:bg-${s.color}-50 hover:border-${s.color}-400 transition-all gap-3 justify-start`}
                    onClick={() => selectSupplierAndOpen(s)} data-testid={`parts-order-supplier-${s.id.toLowerCase()}`}>
                    <img src={s.logo} alt={s.label} className="h-8 object-contain" onError={(e) => { e.target.style.display='none'; }} />
                    <span>{s.label}</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
                  </Button>
                ))}
              </div>
              <div className="pt-2 border-t">
                <Button variant="ghost" className="w-full text-slate-500" onClick={submitNothingOrdered} data-testid="parts-order-nothing-btn">
                  Rien commander — Continuer le travail
                </Button>
              </div>
            </div>
          )}

          {partsOrderStep === 'order_number' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800">Vous avez ouvert le site <strong>{partsOrderSupplier}</strong>.</p>
                <p className="text-sm text-amber-700">Entrez le numero de commande obtenu ou cliquez "Rien commande".</p>
              </div>
              <div>
                <Label className="font-bold">Numero de commande *</Label>
                <Input value={partsOrderNumber} onChange={(e) => setPartsOrderNumber(e.target.value)} placeholder="Ex: ESA-2026-12345"
                  className="h-12 text-lg font-mono" autoFocus data-testid="parts-order-number-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') submitPartsOrder(); }} />
              </div>
              <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" onClick={submitPartsOrder} data-testid="parts-order-submit-btn">
                <Check className="w-5 h-5 mr-2" /> Enregistrer la commande
              </Button>
              <Button variant="ghost" className="w-full text-slate-500" onClick={submitNothingOrdered} data-testid="parts-order-nothing-btn-2">
                Rien commande — Continuer le travail
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delivery Note Dialog */}
      <Dialog open={showDeliveryNoteDialog} onOpenChange={setShowDeliveryNoteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Bulletin de livraison
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Des commandes de pieces existent sur cette fiche. Entrez le numero du bulletin de livraison avant de marquer le vehicule pret.</p>
            <div>
              <Label className="font-bold">N° bulletin de livraison *</Label>
              <Input value={deliveryNoteNumber} onChange={(e) => setDeliveryNoteNumber(e.target.value)} placeholder="Ex: BL-2026-001"
                className="h-12 text-lg font-mono" autoFocus data-testid="delivery-note-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && deliveryNoteNumber.trim()) markVehicleReadyWithNote(deliveryNoteNumber.trim()); }} />
            </div>
            <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" onClick={() => markVehicleReadyWithNote(deliveryNoteNumber.trim())} disabled={!deliveryNoteNumber.trim()} data-testid="delivery-note-submit-btn">
              <Check className="w-5 h-5 mr-2" /> Vehicule PRET
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer */}
      {showViewer && (
        <PhotoViewer photos={viewerPhotos} initialIndex={viewerIndex} onClose={() => setShowViewer(false)} />
      )}
    </>
  );
};
